// Copyright 2025, Imprivata, Inc.  All rights reserved.

import type React from 'react';
import type { TableColumnsType } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { Button, ImprList, type ImprListProps } from '@imprivata-cloud/components';
import EditIcon from '../../../../assets/icons/edit.svg?react';
import UnlockImg from '../../../../assets/unlock.svg?react';
import ItemList from './ItemList';
import { useSession, isCurrentUser } from '../../../../common/authorization/context/Session';
import RowMenuButton from './RowMenuButton';
import { isAdmin, type ImprUser, type User } from '../../../../api/types';

import DeleteImg from '../../../../assets/icons/delete.svg?react';
import './ImprListView.less';

export interface MenuButtonProps {
  enableMenu: boolean;
  menuIcon?: React.ReactNode;
  menuTitle?: string;
  menuClick?: () => void;
  enableEdit?: boolean;
  editUser?: (userId: string) => void;
  deleteUser?: (userId: string) => void;
  unlockUser?: (userId: string) => void;
  adminDeleteDisabled?: boolean;
  enableUnlock?: (user: ImprUser) => boolean;
  unlockPending?: boolean;
  isUserUnlocked?: (userId: string) => boolean;
}

const ImprListView = <T extends Record<string, unknown>>({ rowKey, ...props }: ImprListProps<T> & MenuButtonProps) => {
  const { userInfo: sessionUserInfo } = useSession();

  const columns: TableColumnsType<Record<string, unknown>> = [
    {
      title: 'Display Name',
      dataIndex: 'displayName',
      key: 'displayName',
      defaultSortOrder: 'ascend',
      render: (_, data) => (
        <ItemList
          name={data.displayName as string}
          upn={(data as User).upn || (data as ImprUser).username}
          isAdmin={isAdmin(data as ImprUser)}
          rowMenu={
            <>
              {/* Unlock user */}
              {props.enableUnlock?.(data as ImprUser) && (
                <Button
                  key={`unlock-${data.userId}`}
                  icon={props.isUserUnlocked?.(data.userId as string) ? <CheckOutlined /> : <UnlockImg />}
                  loading={props.unlockPending}
                  label={props.isUserUnlocked?.(data.userId as string) ? 'Unlocked' : 'Unlock'}
                  type="text"
                  className={'unlock-button'}
                  onClick={() => {
                    props.unlockUser?.(data.userId as string);
                  }}
                  aria-label="unlock-user-button"
                  style={props.isUserUnlocked?.(data.userId as string) ? { color: '#30961F' } : undefined}
                />
              )}

              {/* Edit row */}
              {props.enableEdit && (
                <Button
                  key={`edit-${data.userId}`}
                  icon={<EditIcon fill={'#566579'} />}
                  label="Edit"
                  type="text"
                  onClick={() => {
                    if (props.editUser) {
                      props.editUser(data.userId as string);
                    }
                  }}
                  aria-label="edit-user-button"
                  className={'edit-button'}
                />
              )}
              {/* Delete row */}
              {props.deleteUser && (
                <Button
                  key={`delete-${data.userId}`}
                  icon={<DeleteImg />}
                  label="Delete"
                  type="text"
                  onClick={() => props.deleteUser?.(data.userId as string)}
                  aria-label="delete-user-button"
                  disabled={
                    isCurrentUser((data as ImprUser).username, sessionUserInfo) ||
                    (props.adminDeleteDisabled && isAdmin(data as ImprUser))
                  }
                />
              )}

              {/* 3 dots menu */}
              {props.enableMenu && (
                <RowMenuButton
                  key={`row-menu-${data.userId}`}
                  enableMenu={props.enableMenu}
                  menuIcon={props.menuIcon}
                  menuTitle={props.menuTitle}
                  menuClick={props.menuClick}
                  userId={data.userId as string}
                />
              )}
            </>
          }
        />
      ),
    },
  ];

  return (
    <>
      <ImprList
        sticky
        columns={columns}
        dataSource={props.dataSource}
        showHeader={false}
        sortLabel={props.sortLabel}
        customEmptyListNode={props.customEmptyListNode}
        pagination={false}
        rowKey={rowKey as string}
        handleSearch={props.handleSearch}
        headerExtraComponents={props.headerExtraComponents}
        onSearchChange={props.onSearchChange}
      />
    </>
  );
};

export default ImprListView;
